// ─────────────────────────────────────────────────────────────────────
// A PLAYER'S OWN TOURNAMENT HISTORY.
//
// Moved out of apps/web/src/engine/playerDb.ts so the API can run it too: the
// member app's only consumer of the tournament archive is this function, scoped
// to one member, so the server can compute the answer instead of shipping every
// club tournament to a phone.
//
// MOVED VERBATIM. The body below is byte-identical to the original — verified by
// diff, not by eye. Retyping it during a move would mix a behaviour risk into a
// mechanical change, and this is scoring/history logic (docs/architecture/DOMAIN_RULES.md §1).
//
// The rest of playerDb.ts stays in the browser app on purpose: buildPlayerDB()
// generates demo players and runs at module scope, which has no business
// executing on a server.
// ─────────────────────────────────────────────────────────────────────
import { getTeamPlayerIds } from './padel24.js';

export function getPlayerTournamentHistory(
  playerId: any,
  archive: any,
  playerName: any = null,
): any[] {
  const history = [];
  (archive || []).forEach((item) => {
    const t = item.data;
    if (!t) return;

    // ── Klubbmesterskap + Seriespill: pairs (p1Id/p2Id) + matches with team.pairId ──
    // Normalized to a flat profile format (score1/2, eloChange1/2, completed, flat
    // teams with names) so that stats, ELO graph, AllMatchesView and PlayerMatchRow work
    // the same as for Padel24/Americano.
    if (t.type === 'klubbmesterskap' || t.type === 'seriespill') {
      const pairs = t.pairs || [];
      // Pair ids can collide across divisions in old data, so always look up
      // pairs WITHIN the correct division. Also scopes on name (if known) against player-id collision.
      const slotMatchesPlayer = (pid, pname) =>
        pid === playerId && (!playerName || pname === playerName);
      const inPair = pairs.some(
        (p) => slotMatchesPlayer(p.p1Id, p.p1Name) || slotMatchesPlayer(p.p2Id, p.p2Name),
      );
      if (!inPair) return;
      const pairLookup = (pairId, divisionId) =>
        pairs.find((p) => p.id === pairId && p.divisionId === divisionId) ||
        pairs.find((p) => p.id === pairId);
      const pairHasPlayer = (pairId, divisionId) => {
        const p = pairLookup(pairId, divisionId);
        return p && (slotMatchesPlayer(p.p1Id, p.p1Name) || slotMatchesPlayer(p.p2Id, p.p2Name));
      };
      const rawMatches = (t.matches || []).filter(
        (m) =>
          m &&
          (pairHasPlayer(m.team1?.pairId, m.divisionId) ||
            pairHasPlayer(m.team2?.pairId, m.divisionId)),
      );
      // Season NUMBER for seriespill (S1, S2…): the division's season, else the class's
      // currentSeason, else the series' currentSeason, else 1.
      const divSeason = new Map();
      const classSeason = new Map();
      if (t.type === 'seriespill') {
        (t.classes || []).forEach((cls) => {
          classSeason.set(cls.id, cls.currentSeason || 1);
          (cls.divisions || []).forEach((d) => {
            if (d.season != null) divSeason.set(d.id, d.season);
          });
        });
      }
      const seasonNumFor = (m) =>
        t.type === 'seriespill'
          ? (divSeason.get(m.divisionId) ?? classSeason.get(m.classId) ?? t.currentSeason ?? 1)
          : null;
      const matches = rawMatches.map((m) => {
        const a = pairLookup(m.team1?.pairId, m.divisionId);
        const b = pairLookup(m.team2?.pairId, m.divisionId);
        const sNum = seasonNumFor(m);
        return {
          ...m,
          team1: {
            pairId: m.team1?.pairId,
            p1Id: a?.p1Id,
            p2Id: a?.p2Id,
            p1Name: a?.p1Name,
            p2Name: a?.p2Name,
          },
          team2: {
            pairId: m.team2?.pairId,
            p1Id: b?.p1Id,
            p2Id: b?.p2Id,
            p1Name: b?.p1Name,
            p2Name: b?.p2Name,
          },
          score1: m.score ? m.score.t1 : undefined,
          score2: m.score ? m.score.t2 : undefined,
          completed: !!m.score,
          eloChange1: m.teamEloChange?.t1 ?? null,
          eloChange2: m.teamEloChange?.t2 ?? null,
          teamElo1Pre: m.teamEloPre?.t1,
          teamElo2Pre: m.teamEloPre?.t2,
          seasonNum: sNum,
          seasonLabel: sNum != null ? `Sesong ${sNum}` : null,
          playedAt: m.playedAt || m.completedAt || m.scoredAt || null,
        };
      });
      // A lightweight player object for display (name from the pair)
      const myPair = pairs.find(
        (p) => slotMatchesPlayer(p.p1Id, p.p1Name) || slotMatchesPlayer(p.p2Id, p.p2Name),
      );
      const pName = myPair
        ? slotMatchesPlayer(myPair.p1Id, myPair.p1Name)
          ? myPair.p1Name
          : myPair.p2Name
        : playerName || '';

      if (t.type === 'seriespill') {
        // One entry PER SEASON: «Seriespill 2026 - Sesong 1», «… - Sesong 2» etc.
        // The wrapper only gets that season's matches, so that standings/placement is computed per
        // season. When the series rolls over to the next season it automatically becomes a new entry.
        const baseName = t.config?.name || t.name || 'Seriespill';
        const seasonSet = [...new Set(matches.map((m) => m.seasonNum).filter((s) => s != null))];
        if (seasonSet.length === 0) seasonSet.push(t.currentSeason || 1);
        seasonSet.sort((a: any, b: any) => a - b);
        seasonSet.forEach((season) => {
          const seasonNorm = matches.filter(
            (m) => (m.seasonNum ?? (t.currentSeason || 1)) === season,
          );
          const seasonRaw = rawMatches.filter((m) => seasonNumFor(m) === season);
          const seasonName = `${baseName} - Sesong ${season}`;
          const wrapper = {
            ...t,
            name: seasonName,
            config: { ...t.config, name: seasonName },
            matches: seasonRaw,
          };
          history.push({
            key: `${item.key}:s${season}`,
            tournament: wrapper,
            playerInTournament: { id: playerId, name: pName },
            matches: seasonNorm,
            finalPosition: null,
          });
        });
        return;
      }

      history.push({
        key: item.key,
        tournament: t,
        playerInTournament: { id: playerId, name: pName },
        matches,
        finalPosition: null,
      });
      return;
    }

    if (!Array.isArray(t.players)) return;
    const playerInTournament = t.players.find((p) => p.id === playerId);
    if (!playerInTournament) return;

    // Find the matches the player took part in.
    // Uses getTeamPlayerIds, which correctly handles both Padel24 (slot format) and
    // Americano/single matches (flat format).
    const matches = (t.matches || []).filter((m) => {
      if (!m) return false;
      const [t1p1, t1p2] = getTeamPlayerIds(m.team1);
      const [t2p1, t2p2] = getTeamPlayerIds(m.team2);
      return [t1p1, t1p2, t2p1, t2p2].includes(playerId);
    });

    // Find the final placement if the tournament is finished.
    // New logic: each final ranks 2 positions. Final N → winners at
    // (2N-1), losers at (2N). Final 1 = 1st/2nd place, Final 2 = 3rd/4th etc.
    let finalPosition = null;
    if (Array.isArray(t.matches)) {
      const finals = t.matches
        .filter((m) => m.isFinal && m.completed)
        .sort((a, b) => (a.finalNumber || 0) - (b.finalNumber || 0));

      for (const f of finals) {
        const fn = f.finalNumber;
        const basePos = (fn - 1) * 2 + 1;
        const [t1p1, t1p2] = getTeamPlayerIds(f.team1);
        const [t2p1, t2p2] = getTeamPlayerIds(f.team2);
        const t1Has = [t1p1, t1p2].includes(playerId);
        const t2Has = [t2p1, t2p2].includes(playerId);
        if (!t1Has && !t2Has) continue;

        const t1Won = f.score1 > f.score2;
        const playerOnWinningTeam = (t1Has && t1Won) || (t2Has && !t1Won);
        finalPosition = playerOnWinningTeam ? basePos : basePos + 1;
        break;
      }
    }

    history.push({
      key: item.key,
      tournament: t,
      playerInTournament,
      matches,
      finalPosition,
    });
  });

  // Sort newest first — use archivedAt for archived ones, or the last match's playedAt/completedAt for active ones
  history.sort((a, b) => {
    const ta =
      a.tournament.archivedAt ||
      Math.max(0, ...(a.matches || []).map((m) => m.playedAt || m.completedAt || 0));
    const tb =
      b.tournament.archivedAt ||
      Math.max(0, ...(b.matches || []).map((m) => m.playedAt || m.completedAt || 0));
    return tb - ta;
  });
  return history;
}
